import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, CalendarClock, Landmark, ShieldCheck, LogIn, ShieldAlert } from "lucide-react";
import api from "../lib/api";

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function SecurityCard({ security }) {
  if (!security) return null;
  const items = security.items || [];
  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="dashboard-security-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)] flex items-center gap-2">
          <ShieldCheck size={17} /> Security — Admin Portal Access
        </h2>
        <div className="flex items-center gap-2 text-[11.5px] font-[Poppins] font-semibold">
          <span data-testid="security-signins-24h" className="px-2.5 py-1 rounded-full bg-green-100 text-green-700">{security.signins_24h} sign-in{security.signins_24h === 1 ? "" : "s"} (24h)</span>
          <span data-testid="security-failed-24h" className={`px-2.5 py-1 rounded-full ${security.failed_24h ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{security.failed_24h} failed (24h)</span>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-[13.5px] text-slate-400" data-testid="security-empty">No sign-in activity recorded yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {items.map((e) => (
            <li key={e.id} className="py-2.5 flex items-center justify-between gap-3 text-[13px]" data-testid={`security-event-${e.id}`}>
              <span className="flex items-center gap-2.5 min-w-0">
                {e.type === "SIGN_IN"
                  ? <LogIn size={14} className="text-green-600 flex-shrink-0" />
                  : <ShieldAlert size={14} className="text-red-500 flex-shrink-0" />}
                <span className="font-medium text-[var(--pg-navy)] truncate">{e.email}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-semibold whitespace-nowrap ${e.type === "SIGN_IN" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {e.type === "SIGN_IN" ? "Signed in" : `Failed ×${e.count}`}
                </span>
              </span>
              <span className="text-slate-400 text-[12px] whitespace-nowrap">{(e.at || "").slice(0, 16).replace("T", " ")} UTC</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BidDeadlines({ opps }) {
  const upcoming = opps
    .filter((o) => o.due_date && !["WON", "LOST"].includes(o.status))
    .map((o) => ({ ...o, days: Math.ceil((new Date(o.due_date + "T23:59:59") - new Date()) / 86400000) }))
    .filter((o) => o.days <= 14)
    .sort((a, b) => a.days - b.days);
  if (upcoming.length === 0) return null;
  return (
    <div data-testid="dashboard-bid-deadlines" className="mt-6 bg-white rounded-xl border border-amber-200 p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)] flex items-center gap-2">
          <CalendarClock size={17} className="text-amber-500" /> Upcoming Bid Deadlines
        </h2>
        <Link to="/admin/government" data-testid="deadlines-view-board-link" className="text-[12.5px] font-[Poppins] font-semibold text-[var(--pg-accent)] hover:text-[var(--pg-navy)]">View board →</Link>
      </div>
      <ul className="mt-4 space-y-2.5">
        {upcoming.map((o) => (
          <li key={o.id} data-testid={`deadline-item-${o.id}`} className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 rounded-lg px-4 py-2.5 text-[13.5px]">
            <span className="flex items-center gap-2 min-w-0">
              <Landmark size={14} className="text-slate-400 shrink-0" />
              <span className="font-medium text-[var(--pg-navy)] truncate">{o.title}</span>
              <span className="text-slate-400 text-[12.5px] hidden sm:inline">· {o.agency}</span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="text-[12px] text-slate-500">{o.status}</span>
              {o.days < 0 ? (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold bg-red-100 text-red-600">Overdue {Math.abs(o.days)}d</span>
              ) : o.days === 0 ? (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold bg-red-100 text-red-600">Due today</span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold bg-amber-100 text-amber-700">Due in {o.days}d</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [govOpps, setGovOpps] = useState([]);
  const [security, setSecurity] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/admin/gov-opps").then((r) => setGovOpps(r.data)).catch(() => {});
    api.get("/admin/security-events").then((r) => setSecurity(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-slate-500 p-8" data-testid="dashboard-loading">Loading dashboard...</div>;

  const cards = [
    { label: "New RFQs", value: data.new_rfqs, testid: "card-new-rfqs" },
    { label: "Under Review / Sourcing", value: data.under_review, testid: "card-under-review" },
    { label: "Supplier Quotes Pending", value: data.supplier_quotes_pending, testid: "card-supplier-quotes-pending" },
    { label: "Customer Quotes Pending", value: data.customer_quotes_pending, testid: "card-customer-quotes-pending" },
    { label: "Won", value: data.won, testid: "card-won" },
    { label: "Lost", value: data.lost, testid: "card-lost" },
    { label: "Revenue (Accepted)", value: fmtMoney(data.revenue), testid: "card-revenue" },
    { label: "Gross Profit", value: fmtMoney(data.gross_profit), testid: "card-gross-profit" },
    { label: "Avg Gross Margin", value: `${data.avg_gross_margin || 0}%`, testid: "card-avg-margin" },
    { label: "RFQ Conversion", value: `${data.conversion_rate || 0}%`, testid: "card-conversion" },
    { label: "Active Shipments", value: data.active_shipments, testid: "card-active-shipments" },
    { label: "Delivered Orders", value: data.delivered_orders, testid: "card-delivered-orders" },
    { label: "Outstanding Invoices", value: data.outstanding_invoices, testid: "card-outstanding-invoices" },
    { label: "Suppliers", value: data.suppliers, testid: "card-suppliers" },
    { label: "CRM Leads", value: data.leads, testid: "card-leads" },
    { label: "Overdue Deadlines", value: data.overdue_deadlines, testid: "card-overdue-deadlines" },
  ];

  const runReminders = async () => {
    try {
      const { data: res } = await api.post("/admin/quotes/run-reminders");
      const parts = [];
      if (res.reminders_sent?.length) parts.push(`Reminders sent: ${res.reminders_sent.join(", ")}`);
      if (res.expired?.length) parts.push(`Marked expired: ${res.expired.join(", ")}`);
      toast.success(parts.length ? parts.join(" · ") : "No quotes need reminders right now.");
    } catch { toast.error("Failed to run reminders"); }
  };

  const maxMonthly = Math.max(1, ...(data.monthly_rfqs || []).map((m) => m.count));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Dashboard</h1>
        <Link to="/admin/rfqs" data-testid="dashboard-view-rfqs-link" className="btn-primary !py-2.5 !px-4 text-[13px]">View RFQs</Link>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[12px] font-[Poppins] font-semibold uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="mt-2 font-[Poppins] font-extrabold text-[26px] text-[var(--pg-navy)]">{c.value ?? 0}</div>
          </div>
        ))}
        <div data-testid="card-expiring-quotes" className={`rounded-xl border p-5 ${data.expiring_quotes ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <div className="text-[12px] font-[Poppins] font-semibold uppercase tracking-wider text-slate-500">Quotes Expiring Soon</div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="font-[Poppins] font-extrabold text-[26px] text-[var(--pg-navy)]">{data.expiring_quotes ?? 0}</span>
            <button data-testid="run-reminders-button" onClick={runReminders}
              className="inline-flex items-center gap-1.5 text-[11.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] transition-colors">
              <BellRing size={13} /> Send Reminders
            </button>
          </div>
        </div>
      </div>

      <BidDeadlines opps={govOpps} />

      {data.pending_suppliers > 0 && (
        <div data-testid="dashboard-pending-suppliers-alert" className="mt-6 flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-6 py-4">
          <div className="text-[14px] text-blue-900">
            <strong className="font-[Poppins] font-bold">{data.pending_suppliers}</strong> new supplier{data.pending_suppliers === 1 ? "" : "s"} registered and awaiting review.
          </div>
          <Link to="/admin/suppliers" data-testid="pending-suppliers-review-link" className="text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]">
            Review suppliers →
          </Link>
        </div>
      )}

      {data.unassigned_open > 0 && (
        <div data-testid="dashboard-unassigned-alert" className="mt-6 flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4">
          <div className="text-[14px] text-amber-800">
            <strong className="font-[Poppins] font-bold">{data.unassigned_open}</strong> open RFQ{data.unassigned_open === 1 ? "" : "s"} ha{data.unassigned_open === 1 ? "s" : "ve"} no assignee — customers are waiting.
          </div>
          <Link to="/admin/rfqs" data-testid="unassigned-assign-link" className="text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]">
            Assign now →
          </Link>
        </div>
      )}

      <SecurityCard security={security} />

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="dashboard-monthly-chart">
          <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Monthly RFQs</h2>
          <div className="mt-5 flex items-end gap-3 h-40">
            {(data.monthly_rfqs || []).length === 0 && <p className="text-slate-400 text-[14px]">No RFQ data yet.</p>}
            {(data.monthly_rfqs || []).map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
                <span className="text-[12px] font-semibold text-[var(--pg-navy)]">{m.count}</span>
                <div className="w-full bg-[var(--pg-accent)] rounded-t-md" style={{ height: `${Math.max(10, (m.count / maxMonthly) * 100)}px` }}></div>
                <span className="text-[11px] text-slate-500">{m.month}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="dashboard-top-industries">
          <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">Top Industries</h2>
          <ul className="mt-4 space-y-2.5">
            {(data.top_industries || []).length === 0 && <p className="text-slate-400 text-[14px]">No data yet.</p>}
            {(data.top_industries || []).map((i) => (
              <li key={i.industry} className="flex items-center justify-between text-[14px]">
                <span className="text-[var(--pg-navy)] font-medium">{i.industry}</span>
                <span className="font-[Poppins] font-bold text-[var(--pg-accent)]">{i.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
